import { connect, ConnectedProps } from 'react-redux'
import { TwinTableLink } from '../../../../../baseComponents/TwinTable'
import { TableComponentBaseScreenInnerProps } from '../../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { addDictionaryExtraLine, TwinDictionary } from '../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { SellTagsPayload } from '../../../../../utils/reducers/sells/sellTags'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import withLoading from '../../../../../utils/hoc/withLoading'
import { ColumnTableSchema } from '../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { QuoteOrderModelType } from '@teinor/erp/types/company/order/quote/quoteOrder'
import { DisplayPriceGettingParams } from '../../../../../baseComponents/Displays/DisplayPrice'
import { CustomRenderDate, CustomRenderString, CustomRenderStringAndColorNull, EditRow } from '../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { RenderQuoteStatus } from '../../../../../app/erp/screens/Logistic/QuoteParent/subcomponents'
import { RenderSellTags } from '../../../../sells/CustomRenderTable'
import locations from '../../../../../utils/globals/locations'
import { useMemo } from 'react'
import { getEmployees } from '../../../../../utils/reducers/getters'
import { EmployeesPayload } from '../../../../../utils/reducers/company/employees'
import { sortEmployeesByUser } from '../../../../../utils/globals/employees'

type TableCustomerQuoteOrdersProps = TableComponentBaseScreenInnerProps & ReduxTableCustomerQuoteOrders & {
    className?: string
    name?: string
    hideLoadFiltersButton?: boolean
}

const TableCustomerQuoteOrders: React.FC<TableCustomerQuoteOrdersProps> = ({ sellTags, setOpened, setTableInstance, extraWhereParams, ...rest }) => {
    const { t } = useTwinTranslation()
    const quoteOrderStatusComplex: TwinDictionary = {
        0: { name: t('notProcessed', 'Sin procesar'), id: 0 },
        1: { name: t('waiting', 'Pendiente aprovación'), id: 1 },
        2: { name: t('sent', 'Enviado'), id: 2 },
        3: { name: t('accepted', 'Aceptado'), id: 3 },
        4: { name: t('denied', 'Denegado'), id: 4 },
        5: { name: t('processed', 'Procesado'), id: 5 },
    }
    const memoEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(getEmployees() || {}))), [])
    const columns: ColumnTableSchema<QuoteOrderModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('quoteNumberShort', 'Nº Pres.'),
            minWidth: 120,
            sortable: true,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className={allRowData?.assigned_number ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('orderQuoteP', 'Pedido') }
            },
        },
        {
            id: 'version',
            dataKey: 'version',
            label: t('version', 'Versión'),
            sortable: true,
            width: 120,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue || allRowData.version} />,
            hideColumnByDefault: true
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            minWidth: 140,
            sortable: true,
            customRender: (parameterValue) => <RenderQuoteStatus value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: quoteOrderStatusComplex, label: t('statusQuote', 'Estado del presupuesto') }

            },
        },
        {
            id: 'WorkingCenterId',
            dataKey: 'WorkingCenterId',
            searchKey: 'WorkingCenter.name',
            minWidth: 115,
            label: t('workingCenter', 'Centro de trabajo'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.WorkingCenter?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('workingCenter', 'Centro de trabajo') }
            },
            hideColumnByDefault: true
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('createdDate', 'Fecha de creación'),
            minWidth: 165,
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || allRowData.createdAt} className={parameterValue ? '' : 'text-red-BA'} />,
            sortable: true,
        },
        {
            id: 'total_total',
            dataKey: 'total_total',
            label: t('total', 'Total'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'custom_name',
            dataKey: 'custom_name',
            label: t('customNameShort', 'Nombre pers.'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customName', 'Nombre personalizado'), }
            },
            hideColumnByDefault: true
        },
        {
            id: 'total_without_vat',
            dataKey: 'total_without_vat',
            label: t('totalWithoutVatS', 'B. imponible'),
            simpleLabel: t('totalWithoutVat', 'Base imponible'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData.CurrencyId} />,
            hideColumnByDefault: true
        },
        {
            id: 'total_payed',
            dataKey: 'total_payed',
            label: t('totalPayed', 'Total pagado'),
            sortable: true,
            minWidth: 130,
            hideColumnByDefault: true,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'CustomerProjectId',
            dataKey: 'CustomerProjectId',
            label: t('project', 'Proyecto'),
            hideColumnByDefault: true,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.name || ''} />
        },
        {
            id: 'RepresentativeEmployeeId',
            dataKey: 'RepresentativeEmployeeId',
            label: t('representative', 'Representante'),
            searchKey: 'RepresentativeEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.RepresentativeEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: {
                    items: memoEmployees,
                    label: t('representative', 'Representante'),
                    sortItems: true,
                    sortFunction: sortEmployeesByUser
                }
            },
            minWidth: 135,
            searchSingleKey: 'RepresentativeEmployee.id'
        },
        {
            id: 'CreateEmployeeId',
            dataKey: 'CreateEmployeeId',
            label: t('madeBy', 'Realizado por'),
            hideColumnByDefault: true,
            searchKey: 'CreateEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.CreateEmployee ? allRowData?.CreateEmployee?.fullname_short : allRowData?.ProcessEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: {
                    items: memoEmployees,
                    label: t('madeBy', 'Realizado por'),
                    sortItems: true,
                    sortFunction: sortEmployeesByUser
                }
            },
            minWidth: 130,
            searchSingleKey: 'CreateEmployee.id',
        },
        {
            id: 'CustomerProjectType',
            dataKey: 'id',
            hideColumnByDefault: true,
            label: t('projectType', 'Tipo de proyecto'),
            searchKey: 'CustomerProject.CustomerProjectType.name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('projectType', 'Tipo de proyecto') }
            },
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.CustomerProjectType?.name} />
        },
        {
            id: 'CurrentCProjectStatusId',
            dataKey: 'id',
            label: t('projectStatusShort', 'Estado proy.'),
            customRender: (_parameterValue, allRowData) => <CustomRenderStringAndColorNull value={allRowData?.CustomerProject?.CurrentCProjectStatus?.name} color={allRowData?.CustomerProject?.CurrentCProjectStatus?.color} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('status', 'Estado') }
            },
            searchKey: 'CustomerProject.CurrentCProjectStatus.name',
            sortable: true,
            sortKey: 'CustomerProject.CurrentCProjectStatus.order',
            minWidth: 150,
            hideColumnByDefault: true
        },
        {
            id: 'CampaignCustomerId',
            dataKey: 'CampaignCustomerId',
            label: t('campaign', 'Campaña'),
            hideColumnByDefault: true,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CampaignCustomer?.Campaign?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('campaign', 'Campaña') }
            },
            searchKey: 'CampaignCustomer.Campaign.name',
            sortable: true,
            sortKey: 'CampaignCustomer.Campaign.name',
            minWidth: 130,
        },
        {
            id: 'CurrentCCampaignStatusId',
            dataKey: 'id',
            label: t('campaignStatusShort', 'Estado camp.'),
            customRender: (_parameterValue, allRowData) => <CustomRenderStringAndColorNull value={allRowData?.CampaignCustomer?.CurrentCampaignStatus?.name} color={allRowData?.CampaignCustomer?.CurrentCampaignStatus?.color} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('status', 'Estado') }
            },
            searchKey: 'CampaignCustomer.CurrentCampaignStatus.name',
            sortable: true,
            sortKey: 'CampaignCustomer.CurrentCampaignStatus.order',
            minWidth: 160,
            hideColumnByDefault: true
        },
        {
            id: 'finished_date',
            dataKey: 'finished_date',
            label: t('finishedDateLittleShort', 'Fecha finalización'),
            hideColumnByDefault: true,
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue} />,
            sortable: true,
            minWidth: 165,
        },
        {
            id: 'OrderTags',
            dataKey: 'OrderTags',
            label: t('tags', 'Etiquetas'),
            customRender: (_parameterValue, allRowData) => <RenderSellTags {...{ allRowData, sellTags }} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: sellTags?.allTags || {}, label: t('tags', 'Etiquetas') }
            },
            generalSearchable: false,
            sortable: false,
            hideColumnByDefault: true
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.quoteOrderSingle, ['$id']]} columns={columns} name='customerQuoteOrders' getDataFrom='/api/app/order/quote/getAllQuoteOrdersComplete'
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData,
            })} setTableInstance={setTableInstance} extraWhereParams={extraWhereParams} {...rest}
        />
    )
}

const TableCustomerQuoteOrdersDispatch = {
    setTags: (payload: SellTagsPayload) => ({ type: 'CHANGE_SELL_TAGS', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxTableCustomerQuoteOrders = ConnectedProps<typeof tableCustomerQuoteOrdersConnect>
const mapTableCustomerQuoteOrdersConnector = (state: AllReduxPayloads) => ({ sellTags: state.sellTags, employees: state.employees })
const tableCustomerQuoteOrdersConnect = connect(mapTableCustomerQuoteOrdersConnector, TableCustomerQuoteOrdersDispatch)
const TableCustomerQuoteOrdersConnectLoading = withLoading(TableCustomerQuoteOrders, [{ fetchUrl: '/api/app/order/sell/sellTag/getAllSellTags', propName: 'sellTags', setFunctionName: 'setTags' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])
const TableCustomerQuoteOrdersConnect = tableCustomerQuoteOrdersConnect(TableCustomerQuoteOrdersConnectLoading)

export default TableCustomerQuoteOrdersConnect